// src/components/network/feed/RelatedPosts.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building, Clock, MessageSquare, ThumbsUp } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  getDocs, doc, getDoc 
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';

const RelatedPosts = ({ currentPostId, tags = [], authorId }) => {
  const { currentUser } = useAuth();
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        setLoading(true);
        
        // If we have tags, prioritize posts with matching tags
        let postsSnapshot;
        
        if (tags.length > 0) {
          // First try to find posts with at least one matching tag
          const tagsQuery = query(
            collection(db, 'posts'),
            where('tags', 'array-contains-any', tags.slice(0, 10)), // Firestore limits to 10 values
            orderBy('createdAt', 'desc'),
            limit(6)
          );

          const tagsResult = await getDocs(tagsQuery);
          // Exclude current post from results
          postsSnapshot = {
            docs: tagsResult.docs.filter(d => d.id !== currentPostId),
            empty: tagsResult.docs.filter(d => d.id !== currentPostId).length === 0
          };
        }
        
        // If no tags or not enough related posts by tags, get posts from same author
        if (!postsSnapshot || postsSnapshot.empty || postsSnapshot.docs.length < 3) {
          const authorQuery = query(
            collection(db, 'posts'),
            where('authorId', '==', authorId),
            orderBy('createdAt', 'desc'),
            limit(6) // fetch extra to account for excluding current post
          );

          const authorPostsSnapshot = await getDocs(authorQuery);

          // Combine with tag-related posts if any
          const allDocs = [
            ...(postsSnapshot?.docs || []),
            ...authorPostsSnapshot.docs
          ];

          // Remove duplicates, exclude current post, and limit to 5
          const uniqueDocIds = new Set();
          const uniqueDocs = allDocs.filter(doc => {
            if (doc.id === currentPostId) return false;
            if (uniqueDocIds.has(doc.id)) return false;
            uniqueDocIds.add(doc.id);
            return true;
          }).slice(0, 5);

          postsSnapshot = { docs: uniqueDocs };
        }

        // If still no related posts, get latest posts
        if (!postsSnapshot.docs || postsSnapshot.docs.length === 0) {
          const latestQuery = query(
            collection(db, 'posts'),
            orderBy('createdAt', 'desc'),
            limit(6)
          );

          const latestSnapshot = await getDocs(latestQuery);
          postsSnapshot = {
            docs: latestSnapshot.docs.filter(d => d.id !== currentPostId).slice(0, 5)
          };
        }
        
        // Process posts data
        const postsPromises = postsSnapshot.docs.map(async (postDoc) => {
          const postData = postDoc.data();
          
          // Get author data
          let authorData = { id: postData.authorId, name: 'Unknown Business' };
          try {
            const authorRef = doc(db, 'businesses', postData.authorId);
            const authorSnap = await getDoc(authorRef);
            
            if (authorSnap.exists()) {
              authorData = {
                id: authorSnap.id,
                ...authorSnap.data()
              };
            }
          } catch (err) {
            console.error('Error fetching post author:', err);
          }
          
          return {
            id: postDoc.id,
            ...postData,
            createdAt: postData.createdAt?.toDate() || new Date(),
            author: authorData
          };
        });
        
        const posts = await Promise.all(postsPromises);
        setRelatedPosts(posts);
        
      } catch (error) {
        console.error('Error fetching related posts:', error);
        setRelatedPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRelatedPosts();
  }, [currentPostId, tags, authorId]);
  
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center mb-3">
              <div className="h-8 w-8 bg-gray-200 rounded-full mr-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }
  
  if (relatedPosts.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">
        No related posts found
      </p>
    );
  }
  
  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {relatedPosts.map(post => (
        <Link 
          key={post.id} 
          to={`/network/post/${post.id}`}
          className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
        >
          <div className="p-4">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 mr-2 flex-shrink-0 overflow-hidden">
                {post.author.businessLogoUrl ? (
                  <img 
                    src={post.author.businessLogoUrl} 
                    alt={post.author.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                    <Building size={16} />
                  </div>
                )}
              </div>
              <span className="font-medium text-gray-900 text-sm">{post.author.name}</span>
              <span className="mx-2 text-gray-300">•</span>
              <span className="text-gray-500 text-xs flex items-center">
                <Clock size={12} className="mr-1" />
                {formatDate(post.createdAt)}
              </span>
            </div>
            
            <p className="text-gray-800 text-sm line-clamp-2 mb-2">
              {post.content}
            </p>
            
            <div className="flex items-center text-xs text-gray-500">
              {post.likeCount > 0 && (
                <span className="flex items-center mr-3">
                  <ThumbsUp size={12} className="mr-1" />
                  {post.likeCount}
                </span>
              )}
              {post.commentCount > 0 && (
                <span className="flex items-center">
                  <MessageSquare size={12} className="mr-1" />
                  {post.commentCount}
                </span>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default RelatedPosts;